import React, { useState, useEffect } from 'react';
import { X, Search, UserCheck, Users } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const UserSelectionModal = ({ isOpen, onClose, onConfirm, currentUserId, trainingFilter = null, returnFullObjects = false }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [userTrainings, setUserTrainings] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchUsersAndTrainings();
    }
  }, [isOpen]);

  const fetchUsersAndTrainings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;
      
      // Fetch users
      const usersResponse = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Filter out inactive users, company admins, and the current user
      const filteredUsers = usersResponse.data.filter(user => 
        user.is_active && 
        !user.is_company_admin &&
        user.id !== currentUserId
      );

      // Fetch training assignments
      const trainingResponse = await axios.get(`${API_BASE_URL}/user-training-assignments/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Create a map of user trainings
      const trainingsMap = {};
      let usersWithSpecificTraining = new Set();
      
      trainingResponse.data.forEach(assignment => {
        if (assignment.status === 'planned' && assignment.user) {
          // Handle both user object and user ID
          let userId;
          if (typeof assignment.user === 'object' && assignment.user.id) {
            userId = assignment.user.id;
          } else if (typeof assignment.user === 'number') {
            userId = assignment.user;
          } else if (assignment.user_id) {
            userId = assignment.user_id;
          } else {
            userId = assignment.user;
          }
          
          // Ensure userId is a number for consistency
          userId = parseInt(userId);
          
          if (!trainingsMap[userId]) {
            trainingsMap[userId] = [];
          }
          
          const trainingData = {
            trainingName: assignment.training?.qualification_type?.name || assignment.description || 'Training',
            trainingId: assignment.training?.id || null,
            startDate: new Date(assignment.start_date),
            endDate: new Date(assignment.end_date)
          };
          
          trainingsMap[userId].push(trainingData);
          
          // If we have a training filter, track users who have this specific training on the specific date
          if (trainingFilter && trainingFilter.trainingName && trainingFilter.date) {
            const filterDate = new Date(trainingFilter.date);
            const assignmentStartDate = new Date(assignment.start_date);
            
            // Extract just the date parts for comparison (ignore time and timezone)
            const filterDateOnly = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate());
            const assignmentDateOnly = new Date(assignmentStartDate.getFullYear(), assignmentStartDate.getMonth(), assignmentStartDate.getDate());
            
            // Check if training name matches and dates are the same (comparing only date, not time)
            const trainingNameMatches = trainingData.trainingName.toLowerCase() === trainingFilter.trainingName.toLowerCase() ||
                                      (assignment.description && assignment.description.toLowerCase().includes(trainingFilter.trainingName.toLowerCase()));
            
            if (trainingNameMatches && filterDateOnly.getTime() === assignmentDateOnly.getTime()) {
              usersWithSpecificTraining.add(userId);
              console.log(`User ${userId} has matching training: ${trainingData.trainingName} on ${assignmentStartDate}`);
            }
          }
        }
      });

      // Sort trainings by date for each user
      Object.keys(trainingsMap).forEach(userId => {
        trainingsMap[userId].sort((a, b) => a.startDate - b.startDate);
      });

      // If we have a training filter, only show users with that specific training
      let finalUsers = filteredUsers;
      if (trainingFilter && trainingFilter.trainingName && trainingFilter.date) {
        console.log('Training filter:', trainingFilter);
        console.log('Users with specific training:', Array.from(usersWithSpecificTraining));
        console.log('All filtered users:', filteredUsers.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}` })));
        finalUsers = filteredUsers.filter(user => usersWithSpecificTraining.has(user.id));
        console.log('Final filtered users:', finalUsers.map(u => ({ id: u.id, name: `${u.first_name} ${u.last_name}` })));
      }

      setUsers(finalUsers);
      setUserTrainings(trainingsMap);
    } catch (error) {
      console.error('Error fetching users and trainings:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredUserIds = getFilteredUsers().map(user => user.id);
    setSelectedUsers(filteredUserIds);
  };

  const handleDeselectAll = () => {
    setSelectedUsers([]);
  };

  const getFilteredUsers = () => {
    return users.filter(user => {
      const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
      return fullName.includes(searchTerm.toLowerCase());
    });
  };

  const handleConfirm = () => {
    if (selectedUsers.length === 0) {
      toast.warning('Please select at least one user');
      return;
    }
    
    if (returnFullObjects) {
      // Return full user objects for email functionality
      const selectedUserObjects = users.filter(user => selectedUsers.includes(user.id));
      onConfirm(selectedUserObjects);
    } else {
      // Return just IDs (original behavior)
      onConfirm(selectedUsers);
    }
    onClose();
  };

  if (!isOpen) return null;

  const filteredUsers = getFilteredUsers();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Users className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold">
                {trainingFilter ? 'Select Users for Travel' : 'Select Users for Training'}
              </h2>
              {trainingFilter && trainingFilter.trainingName && (
                <p className="text-sm text-gray-600">
                  Users scheduled for {trainingFilter.trainingName} on {new Date(trainingFilter.date).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedUsers.length} of {filteredUsers.length} users selected
            </span>
            <div className="space-x-2">
              <button
                onClick={handleSelectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={handleDeselectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Deselect All
              </button>
            </div>
          </div>
        </div>

        {/* User List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading users...</p>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No users found matching your search criteria
            </div>
          ) : (
            <div className="space-y-2">
              {filteredUsers.map(user => {
                const trainings = userTrainings[user.id] || [];
                const upcomingTrainings = trainings.filter(t => t.startDate >= new Date());
                
                return (
                  <div
                    key={user.id}
                    className={`flex items-start p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedUsers.includes(user.id)
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                    onClick={() => handleUserToggle(user.id)}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      readOnly
                      className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 mt-0.5 pointer-events-none"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-center">
                        <span className="font-medium text-gray-900">
                          {user.first_name} {user.last_name}
                        </span>
                        {selectedUsers.includes(user.id) && (
                          <UserCheck className="ml-2 h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      {user.email && (
                        <span className="text-sm text-gray-500">{user.email}</span>
                      )}
                      {upcomingTrainings.length > 0 && (
                        <div className="mt-1 space-y-1">
                          {upcomingTrainings.slice(0, 2).map((training, idx) => (
                            <div key={idx} className="text-xs">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                                <span className="font-medium">{training.trainingName}</span>
                                <span className="ml-1 text-green-600">
                                  {training.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </span>
                            </div>
                          ))}
                          {upcomingTrainings.length > 2 && (
                            <span className="text-xs text-gray-500 italic">
                              +{upcomingTrainings.length - 2} more trainings
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end space-x-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedUsers.length === 0}
            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors ${
              selectedUsers.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            Schedule for {selectedUsers.length} {selectedUsers.length === 1 ? 'User' : 'Users'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSelectionModal;